// File: frontend/src/components/ui/EmptyState.js
//
// Calm, helpful empty state, generalised from the Vouchers page. Icon in a soft
// accent ring, a title, an explanation, and an optional action. Theme-aware in
// both modes (accent defaults to the brand primary).
//
// §13.3 EXTENSION — the four cases, without breaking the three call sites that
// already use this component.
//
//   Every prop this component had before still means exactly what it meant, and
//   a caller that passes `{ title, description }` and nothing else renders
//   byte-identically to before. What is NEW is the `kind` prop: pass one of
//   EMPTY_KIND and the component takes its icon, its accent and its default
//   wording from ui/emptyStates.js, so a first-run tenant, a quiet period, an
//   over-filtered view and a failed read stop looking like each other.
//
//   The tone is never the ONLY difference (§13.4, no colour-only encoding):
//   each kind brings its own icon and its own sentence too, and the unavailable
//   and error kinds additionally carry a visually-hidden prefix naming the
//   state, so a screen reader hears "Unavailable:" before the title rather than
//   having to infer it from an amber ring it cannot see.

import React from "react";
import { Box, Typography, useTheme, alpha } from "@mui/material";
import InboxIcon from "@mui/icons-material/Inbox";

import { SR_ONLY } from "./tokens";
import { resolveEmptyState, EMPTY_KIND } from "./emptyStates";

// Tone name → palette resolver. `neutral` has no palette entry of its own and
// deliberately reads as text, not as a status.
const TONE = {
  primary: (t) => t.palette.primary.main,
  info: (t) => t.palette.info.main,
  warning: (t) => t.palette.warning.main,
  error: (t) => t.palette.error.main,
  success: (t) => t.palette.success.main,
  neutral: (t) => t.palette.text.secondary,
};

// What a screen reader hears before the title, for the two kinds where mistaking
// the state for "there are none" leads to a wrong decision.
const SR_PREFIX = {
  [EMPTY_KIND.unavailable]: "Unavailable: ",
  [EMPTY_KIND.error]: "Error: ",
  [EMPTY_KIND.noPermission]: "No access: ",
};

export default function EmptyState({
  icon: Icon,
  title,
  description,
  action,           // optional node (e.g. a Button)
  accent,           // optional accent override (a colour string)

  // ── §13.3 additions, all optional ──────────────────────────────────────────
  kind,             // one of EMPTY_KIND — supplies icon, tone and default words
  subject,          // the plural noun used in the default wording
  hint,             // the next action, in words; pass null to suppress it
  reason,           // the server's own words, rendered after the body
  filterSummary,    // for kind="filtered": which filters are in force
  tone,             // explicit tone override: primary|info|warning|error|neutral

  dense = false,
  sx,
}) {
  const theme = useTheme();

  // Resolve only when a kind was asked for. Without one, every default below is
  // exactly what this component did before, so no existing consumer moves.
  const resolved = kind
    ? resolveEmptyState({ kind, subject, title, body: description, hint, reason, filterSummary })
    : null;

  const finalTitle = resolved ? resolved.title : title;
  const finalDescription = resolved ? resolved.description : description;
  const FinalIcon = Icon || resolved?.icon || InboxIcon;

  const toneKey = tone || resolved?.tone;
  const color = accent
    || (toneKey ? (TONE[toneKey] || TONE.neutral)(theme) : theme.palette.primary.main);

  const srPrefix = resolved ? SR_PREFIX[resolved.kind] : null;

  return (
    <Box
      sx={{
        py: dense ? 4 : 7,
        px: 3,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        textAlign: "center",
        ...sx,
      }}
      // Grouped and named, so the whole block is reachable as one thing rather
      // than as three loose paragraphs in the middle of a table.
      role="group"
      aria-label={finalTitle ? `${srPrefix || ""}${finalTitle}` : undefined}
    >
      <Box
        sx={{
          width: 64, height: 64, borderRadius: "50%",
          bgcolor: alpha(color, 0.1),
          border: `1px dashed ${alpha(color, 0.35)}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <FinalIcon sx={{ fontSize: 28, color: alpha(color, 0.7) }} aria-hidden />
      </Box>
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: 15, color: "text.primary", mb: 0.5 }}>
          {srPrefix && <Box component="span" sx={SR_ONLY}>{srPrefix}</Box>}
          {finalTitle}
        </Typography>
        {finalDescription && (
          <Typography sx={{ fontSize: 13, color: "text.secondary", maxWidth: 340, mx: "auto", lineHeight: 1.55 }}>
            {finalDescription}
          </Typography>
        )}
      </Box>
      {action}
    </Box>
  );
}
